import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

export default function Certificates() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/ai/certificates")
      .then((r) => setCerts(r.data.certificates))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      <div className="flex items-center gap-3 mb-8">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Award className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">Your certificates</h1>
          <p className="text-muted-foreground">Every module you've completed on Tutors Paradise.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : certs.length === 0 ? (
        <Card className="p-12 text-center">
          <Award className="h-10 w-10 mx-auto text-muted-foreground" />
          <h3 className="mt-4 font-heading text-xl font-semibold">No certificates yet</h3>
          <p className="text-muted-foreground mt-1 max-w-md mx-auto">
            Finish an AI Practice module and score 80% or higher to earn your first certificate.
          </p>
          <Button asChild className="mt-5 rounded-full">
            <Link to="/ai-practice">Go to AI Practice</Link>
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {certs.map((c) => (
            <Card key={c.id} className="p-6 flex flex-col gap-3" data-testid="certificate-card">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-100 to-amber-50 dark:from-amber-900/60 dark:to-amber-950/60 flex items-center justify-center">
                  <Award className="h-6 w-6 text-amber-600 dark:text-amber-300" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading text-lg font-semibold truncate">{c.module_title}</h3>
                  <p className="text-xs text-muted-foreground">{c.subject}</p>
                </div>
                <Badge variant="outline" className="rounded-full font-mono text-xs">
                  {c.score}%
                </Badge>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Issued {new Date(c.issued_at).toLocaleDateString()}</span>
                <span className="font-mono">{c.code}</span>
              </div>
              <Button asChild variant="outline" size="sm" className="rounded-full mt-1">
                <Link to={`/certificates/${c.id}`} data-testid="certificate-view-button">
                  View certificate
                </Link>
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
